/**
 * ClaimPage - Non-Custodial Claim Link System (Peanut-Style)
 * 
 * UX Goal: Click link -> See balance -> Verify email -> Withdraw to Bank -> Done
 * 
 * Architecture:
 * 1. Sender pays fiat -> Guardarian -> USDC deposited to smart contract
 * 2. Recipient clicks link -> Sees amount
 * 3. Recipient verifies email -> Embedded wallet created via Dynamic.xyz
 * 4. Recipient clicks "Withdraw to Bank" -> Off-ramp widget opens (Transak/Onramper)
 * 5. Recipient enters bank details with off-ramp provider -> Money in bank
 * 
 * GOLDEN RULES:
 * - NO crypto jargon visible to user
 * - NO private keys on our servers
 * - Recipient initiates final withdrawal (First-Party, not Third-Party)
 * 
 * Legal: This converts "Third-Party Payout" into "First-Party Withdrawal"
 */

import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useDynamicContext } from '@dynamic-labs/sdk-react-core';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Loader2, 
  CheckCircle2, 
  AlertCircle, 
  ArrowRight, 
  Banknote,
  Shield,
  Clock,
  Gift,
  Mail,
  Building2,
  Sparkles,
  ExternalLink,
  Copy,
  Check,
  Wallet
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Off-ramp configuration - Transak or Onramper
const TRANSAK_API_KEY = process.env.REACT_APP_TRANSAK_API_KEY || '';
const ONRAMPER_API_KEY = process.env.REACT_APP_ONRAMPER_API_KEY || 'pk_prod_demo';

// Flow steps
const STEPS = {
  LOADING: 'loading',
  SHOW_AMOUNT: 'show_amount',       // Step 1: Show "You received $X"
  ENTER_EMAIL: 'enter_email',        // Step 2: Enter email
  VERIFY_CODE: 'verify_code',        // Step 3: Enter verification code
  WALLET_CREATED: 'wallet_created',  // Step 4: Wallet created, show balance
  SELECT_METHOD: 'select_method',    // Step 5: Select withdrawal method
  OFFRAMP_WIDGET: 'offramp_widget',  // Step 6: Show off-ramp widget
  PROCESSING: 'processing',          // Processing state
  SUCCESS: 'success',                // Done!
  ERROR: 'error'
};

// Off-ramp providers available through Onramper
const OFFRAMP_PROVIDERS = [
  {
    id: 'transak',
    name: 'Bank Transfer',
    description: 'Transfer directly to your bank account',
    time: '1-3 business days',
    available: !!TRANSAK_API_KEY,
    icon: Building2,
    recommended: true
  },
  {
    id: 'onramper',
    name: 'Multiple Options',
    description: 'Choose from MoonPay, Transak & more',
    time: '1-3 business days',
    available: true,
    icon: Sparkles
  }
];

export default function ClaimPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Dynamic.xyz context for embedded wallet
  const { setShowAuthFlow, primaryWallet, user: dynamicUser } = useDynamicContext();
  
  // URL params (Peanut-style: ?c=chain&i=link_id&p=secret)
  const chain = searchParams.get('c') || 'polygon';
  const linkId = searchParams.get('i') || searchParams.get('token');
  const secret = searchParams.get('p');
  
  // State
  const [step, setStep] = useState(STEPS.LOADING);
  const [linkData, setLinkData] = useState(null);
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [walletAddress, setWalletAddress] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showWidget, setShowWidget] = useState(false);

  // Update wallet address when Dynamic wallet is available
  useEffect(() => {
    if (primaryWallet?.address) {
      setWalletAddress(primaryWallet.address);
    }
  }, [primaryWallet]);

  // Fetch link details on mount
  useEffect(() => {
    if (!linkId) {
      setStep(STEPS.ERROR);
      setError('Invalid link - no transfer found');
      return;
    }
    fetchLinkDetails();
  }, [linkId]);

  const fetchLinkDetails = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/claims/${linkId}?p=${secret || ''}`);
      
      // Read response body only once
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        // If JSON parsing fails, show a generic error
        setStep(STEPS.ERROR);
        setError('This link is invalid or expired');
        return;
      }
      
      if (!response.ok) {
        setStep(STEPS.ERROR);
        setError(data?.detail || 'This link is invalid or expired');
        return;
      }
      
      setLinkData(data);
      
      // Check if already claimed
      if (data.status === 'claimed' || data.status === 'withdrawn') {
        setStep(STEPS.ERROR);
        setError('This transfer has already been claimed');
        return;
      }
      
      if (data.status === 'expired') {
        setStep(STEPS.ERROR);
        setError('This link has expired');
        return;
      }
      
      setStep(STEPS.SHOW_AMOUNT);
    } catch (err) {
      setStep(STEPS.ERROR);
      // Show user-friendly error message, not technical errors
      if (err.message && !err.message.includes('clone') && !err.message.includes('Response')) {
        setError(err.message);
      } else {
        setError('This link is invalid or expired');
      }
    }
  };

  const handleContinueToEmail = () => {
    setStep(STEPS.ENTER_EMAIL);
  };

  const handleSendCode = async () => {
    if (!email) {
      setError('Please enter your email');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/claims/send-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          link_id: linkId,
          secret: secret,
          recipient_email: email
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send code');
      }
      
      setStep(STEPS.VERIFY_CODE);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode.length < 6) {
      setError('Please enter the 6-digit code');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // First verify the code
      const verifyResponse = await fetch(`${BACKEND_URL}/api/claims/verify-code?email=${encodeURIComponent(email)}&code=${verificationCode}&link_id=${linkId}`, {
        method: 'POST'
      });
      
      if (!verifyResponse.ok) {
        const data = await verifyResponse.json();
        throw new Error(data.detail || 'Invalid code');
      }
      
      // Get wallet address from Dynamic or generate one
      let recipientWallet = primaryWallet?.address;
      
      if (!recipientWallet) {
        // Generate a deterministic wallet address based on email (simulated)
        // In production, Dynamic creates real embedded wallets
        recipientWallet = '0x' + Array(40).fill(0).map((_, i) => 
          ((email.charCodeAt(i % email.length) + i) % 16).toString(16)
        ).join('');
      }
      
      // Claim to wallet
      const claimResponse = await fetch(`${BACKEND_URL}/api/claims/claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          link_id: linkId,
          secret: secret,
          recipient_email: email,
          recipient_wallet: recipientWallet
        })
      });
      
      const claimData = await claimResponse.json();
      
      if (!claimResponse.ok) {
        throw new Error(claimData.detail || 'Failed to claim');
      }
      
      setWalletAddress(recipientWallet);
      setStep(STEPS.WALLET_CREATED);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleWithdrawToBank = () => {
    setStep(STEPS.SELECT_METHOD);
  };

  const handleSelectProvider = async (provider) => {
    if (!provider.available) return;
    
    setSelectedProvider(provider);
    
    if (provider.id === 'onramper') {
      // Show Onramper widget inline
      setStep(STEPS.OFFRAMP_WIDGET);
      setShowWidget(true);
    } else {
      // For other providers, call backend to get redirect URL
      setStep(STEPS.PROCESSING);
      
      try {
        const response = await fetch(`${BACKEND_URL}/api/claims/initiate-withdraw`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            link_id: linkId,
            provider: provider.id,
            wallet_address: walletAddress
          })
        });
        
        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.detail || 'Failed to initiate withdrawal');
        }
        
        // Open off-ramp widget in new tab
        if (data.redirect_url) {
          window.open(data.redirect_url, '_blank');
        }
        
        setStep(STEPS.SUCCESS);
      } catch (err) {
        setError(err.message);
        setStep(STEPS.ERROR);
      }
    }
  };

  const copyToClipboard = useCallback((text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, []);

  // Generate Transak off-ramp widget URL
  const getTransakWidgetUrl = () => {
    const amount = linkData?.amount || 100;
    const params = new URLSearchParams({
      apiKey: TRANSAK_API_KEY,
      environment: 'PRODUCTION',
      cryptoCurrencyCode: 'USDC',
      network: 'polygon',
      defaultCryptoAmount: amount.toString(),
      walletAddress: walletAddress || '',
      isFeeCalculationHidden: 'false',
      hideMenu: 'true',
      themeColor: '059669',
      productsAvailed: 'SELL'
    });
    
    return `https://global.transak.com?${params.toString()}`;
  };

  // Generate Onramper off-ramp widget URL
  const getOnramperWidgetUrl = () => {
    const amount = linkData?.amount || 100;
    const params = new URLSearchParams({
      apiKey: ONRAMPER_API_KEY,
      mode: 'sell',  // Off-ramp mode
      defaultCrypto: 'usdc_polygon',
      defaultAmount: amount.toString(),
      walletAddress: walletAddress || '',
      primaryColor: '059669',  // Emerald color
      darkMode: 'false',
      isAddressEditable: 'false',
      supportSell: 'true',
      supportBuy: 'false'
    });
    
    return `https://buy.onramper.com?${params.toString()}`;
  };

  // Get the appropriate widget URL based on selected provider
  const getOffRampWidgetUrl = () => {
    if (selectedProvider?.id === 'transak' && TRANSAK_API_KEY) {
      return getTransakWidgetUrl();
    }
    return getOnramperWidgetUrl();
  };

  // =========================================================================
  // RENDER FUNCTIONS - Clean UI, no crypto jargon
  // =========================================================================

  // Loading
  if (step === STEPS.LOADING) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center shadow-lg">
          <CardContent className="pt-12 pb-12">
            <Loader2 className="w-12 h-12 animate-spin mx-auto text-emerald-600 mb-4" />
            <p className="text-gray-600">Loading your transfer...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error
  if (step === STEPS.ERROR) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center shadow-lg">
          <CardHeader>
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <CardTitle className="text-red-700">Unable to Load</CardTitle>
            <CardDescription>{error || 'This link may be invalid or expired'}</CardDescription>
          </CardHeader>
          <CardFooter>
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => navigate('/')}
              data-testid="go-home-btn"
            >
              Go to Home
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // Step 1: Show amount (like receiving a gift)
  if (step === STEPS.SHOW_AMOUNT) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl border-0">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-20 h-20 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center mb-4 shadow-lg">
              <Gift className="w-10 h-10 text-white" />
            </div>
            <CardTitle className="text-2xl text-gray-800">You Received Money!</CardTitle>
            {linkData?.recipient_name && (
              <CardDescription className="text-base">
                Hi {linkData.recipient_name}!
              </CardDescription>
            )}
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* The Big Number - No crypto jargon! */}
            <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-8 text-center text-white shadow-lg">
              <p className="text-sm opacity-80 mb-2">Amount</p>
              <p className="text-5xl font-bold">
                ${linkData?.amount?.toFixed(2) || '100.00'}
              </p>
              <p className="text-sm opacity-80 mt-2">{linkData?.currency || 'USD'}</p>
            </div>
            
            {/* Optional message */}
            {linkData?.message && (
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-gray-600 italic">"{linkData.message}"</p>
              </div>
            )}
            
            <Button 
              className="w-full h-14 text-lg bg-emerald-600 hover:bg-emerald-700 shadow-md"
              onClick={handleContinueToEmail}
              data-testid="claim-btn"
            >
              Claim Now
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </CardContent>
          
          <CardFooter className="flex-col space-y-2 text-center text-xs text-gray-500 pt-0">
            <div className="flex items-center justify-center gap-4">
              <div className="flex items-center gap-1">
                <Shield className="w-4 h-4 text-emerald-600" />
                <span>Secure</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4 text-emerald-600" />
                <span>Expires in 30 days</span>
              </div>
            </div>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // Step 2: Enter email
  if (step === STEPS.ENTER_EMAIL) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl border-0">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
              <Mail className="w-8 h-8 text-emerald-600" />
            </div>
            <CardTitle>Verify Your Email</CardTitle>
            <CardDescription>
              We'll send a code to confirm it's you
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-4">
            <div className="bg-emerald-50 rounded-lg p-4 text-center mb-4">
              <p className="text-2xl font-bold text-emerald-700">
                ${linkData?.amount?.toFixed(2) || '100.00'}
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Your Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12"
                data-testid="claim-email-input"
              />
            </div>
            
            {error && (
              <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">
                {error}
              </div>
            )}
            
            <Button 
              className="w-full h-12 bg-emerald-600 hover:bg-emerald-700"
              onClick={handleSendCode}
              disabled={loading || !email}
              data-testid="send-code-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  Send Code
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
            
            <Button 
              variant="ghost" 
              className="w-full"
              onClick={() => setStep(STEPS.SHOW_AMOUNT)}
            >
              Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step 3: Enter verification code
  if (step === STEPS.VERIFY_CODE) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl border-0">
          <CardHeader className="text-center">
            <CardTitle>Enter Code</CardTitle>
            <CardDescription>
              We sent a 6-digit code to <span className="font-medium">{email}</span>
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="code">Verification Code</Label>
              <Input
                id="code"
                type="text"
                placeholder="000000"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="text-center text-3xl tracking-[0.5em] h-16 font-mono"
                maxLength={6}
                data-testid="verification-code-input"
              />
            </div>
            
            {error && (
              <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">
                {error}
              </div>
            )}
            
            <Button 
              className="w-full h-12 bg-emerald-600 hover:bg-emerald-700"
              onClick={handleVerifyCode}
              disabled={loading || verificationCode.length < 6}
              data-testid="verify-code-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Verifying...
                </>
              ) : (
                'Verify & Continue'
              )}
            </Button>
            
            <Button 
              variant="ghost" 
              className="w-full"
              onClick={() => {
                setStep(STEPS.ENTER_EMAIL);
                setVerificationCode('');
                setError(null);
              }}
            >
              Use different email
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step 4: Wallet created - Show balance & withdraw button
  if (step === STEPS.WALLET_CREATED) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl border-0">
          <CardHeader className="text-center">
            <div className="mx-auto w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 className="w-10 h-10 text-emerald-600" />
            </div>
            <CardTitle className="text-2xl">Money Claimed!</CardTitle>
            <CardDescription>Now withdraw it to your bank account</CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Balance Display */}
            <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-6 text-center text-white shadow-lg">
              <p className="text-sm opacity-80 mb-1">Your Balance</p>
              <p className="text-4xl font-bold">
                ${linkData?.amount?.toFixed(2) || '100.00'}
              </p>
              <p className="text-sm opacity-80 mt-1">{linkData?.currency || 'USD'}</p>
            </div>
            
            {/* THE MAGIC BUTTON */}
            <Button 
              className="w-full h-14 text-lg bg-emerald-600 hover:bg-emerald-700 shadow-md"
              onClick={handleWithdrawToBank}
              data-testid="withdraw-to-bank-btn"
            >
              <Banknote className="w-5 h-5 mr-2" />
              Withdraw to Bank
            </Button>
            
            <div className="flex items-center justify-center gap-4 text-xs text-gray-500">
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                <span>1-3 business days</span>
              </div>
              <div className="flex items-center gap-1">
                <Shield className="w-4 h-4" />
                <span>Secure transfer</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step 5: Select withdrawal method
  if (step === STEPS.SELECT_METHOD) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl border-0">
          <CardHeader className="text-center">
            <CardTitle>How do you want to receive?</CardTitle>
            <CardDescription>
              Choose how to withdraw ${linkData?.amount?.toFixed(2) || '100.00'}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-3">
            {OFFRAMP_PROVIDERS.map((provider) => {
              const Icon = provider.icon;
              return (
                <button
                  key={provider.id}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                    provider.available
                      ? 'border-gray-200 hover:border-emerald-500 hover:bg-emerald-50 cursor-pointer'
                      : 'border-gray-100 bg-gray-50 cursor-not-allowed opacity-60'
                  } ${provider.recommended ? 'border-emerald-300 bg-emerald-50' : ''}`}
                  onClick={() => handleSelectProvider(provider)}
                  disabled={!provider.available}
                  data-testid={`provider-${provider.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        provider.recommended ? 'bg-emerald-200' : 'bg-emerald-100'
                      }`}>
                        <Icon className="w-6 h-6 text-emerald-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-gray-800">{provider.name}</p>
                          {provider.recommended && (
                            <span className="text-xs bg-emerald-600 text-white px-2 py-0.5 rounded-full">
                              Recommended
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">{provider.description}</p>
                        <p className="text-xs text-emerald-600">{provider.time}</p>
                      </div>
                    </div>
                    {provider.available && (
                      <ArrowRight className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </button>
              );
            })}
            
            <Button 
              variant="ghost" 
              className="w-full mt-4"
              onClick={() => setStep(STEPS.WALLET_CREATED)}
            >
              Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step 6: Off-ramp Widget (Transak or Onramper)
  if (step === STEPS.OFFRAMP_WIDGET) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg shadow-xl border-0">
          <CardHeader className="text-center">
            <CardTitle>Withdraw to Your Bank</CardTitle>
            <CardDescription>
              Complete your withdrawal of ${linkData?.amount?.toFixed(2) || '100.00'}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-4">
            {/* Off-ramp Widget iframe */}
            <div className="rounded-xl overflow-hidden border border-gray-200 bg-white">
              <iframe
                src={getOffRampWidgetUrl()}
                title="Withdraw to Bank"
                allow="accelerometer; autoplay; camera; gyroscope; payment"
                className="w-full h-[500px] border-0"
                data-testid="offramp-widget"
              />
            </div>
            
            {/* Alternative: Open in new tab */}
            <div className="text-center">
              <button
                onClick={() => window.open(getOffRampWidgetUrl(), '_blank')}
                className="text-sm text-emerald-600 hover:underline flex items-center justify-center gap-1"
              >
                <ExternalLink className="w-4 h-4" />
                Open in new window
              </button>
            </div>
            
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => {
                setStep(STEPS.SUCCESS);
              }}
              data-testid="complete-withdrawal-btn"
            >
              I've completed my withdrawal
            </Button>
            
            <Button 
              variant="ghost" 
              className="w-full"
              onClick={() => setStep(STEPS.SELECT_METHOD)}
            >
              Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Processing
  if (step === STEPS.PROCESSING) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center shadow-xl border-0">
          <CardContent className="pt-12 pb-12">
            <Loader2 className="w-12 h-12 animate-spin mx-auto text-emerald-600 mb-4" />
            <p className="text-lg font-medium mb-2">Setting Up Your Withdrawal</p>
            <p className="text-gray-600">
              Connecting to {selectedProvider?.name}...
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success
  if (step === STEPS.SUCCESS) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl border-0">
          <CardHeader className="text-center">
            <div className="mx-auto w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 className="w-10 h-10 text-emerald-600" />
            </div>
            <CardTitle className="text-2xl">Withdrawal Initiated!</CardTitle>
            <CardDescription>
              Your money is on its way to your bank
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-4">
            <div className="bg-gray-50 rounded-xl p-4 space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Amount</span>
                <span className="font-semibold">${linkData?.amount?.toFixed(2) || '100.00'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Method</span>
                <span className="font-semibold">{selectedProvider?.name || 'Bank Transfer'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Expected arrival</span>
                <span className="font-semibold">{selectedProvider?.time || '1-3 business days'}</span>
              </div>
            </div>
            
            <div className="bg-blue-50 rounded-lg p-4 text-sm text-blue-700">
              <p className="font-medium mb-1">What happens next?</p>
              <ol className="list-decimal list-inside space-y-1 text-blue-600">
                <li>Your withdrawal is being processed</li>
                <li>You'll receive an email confirmation</li>
                <li>Money arrives in your bank in 1-3 days</li>
              </ol>
            </div>
            
            <Button 
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              onClick={() => navigate('/')}
              data-testid="done-btn"
            >
              Done
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
}
